import React from 'react';

// Função para formatar nome com apenas a primeira letra maiúscula
const capitalizeFirst = (str) => {
  if (!str || typeof str !== 'string' || str === '—') return str || '—';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const MonthlyFinancialTable = ({
  data = { summary: [], revenueTypes: [], expenseTypes: [] },
}) => {
  const months = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  // Calcular totais do resumo geral
  const totalReceita = data.summary.reduce((sum, item) => sum + (item && item.receita ? item.receita : 0), 0);
  const totalDespesa = data.summary.reduce((sum, item) => sum + (item && item.despesa ? item.despesa : 0), 0);
  
  // Garantir que revenueTypes e expenseTypes existem
  const revenueTypes = data.revenueTypes || [];
  const expenseTypes = data.expenseTypes || [];

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-foreground mb-4">
        Resumo Financeiro
      </h3>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border border-border bg-secondary/30">
              <th className="text-left py-2 px-4 font-semibold text-xs text-muted border-r border-border">
                Categoria
              </th>
              {months.map((month) => (
                <th key={month} className="text-right py-2 px-4 font-semibold text-xs text-muted">
                  {month.substring(0, 3)}
                </th>
              ))}
              <th className="text-right py-2 px-4 font-semibold text-xs text-muted">
                Total
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Linha vazia de separação*/}
            <tr className="border border-border bg-secondary-light/30 dark:bg-secondary-dark/30">
              <td className="py-1 px-4 border-r border-border"></td>
              {months.map((month, idx) => (
                <td key={idx} className="py-1 px-4"></td>
              ))}
              <td className="py-1 px-4"></td>
            </tr>
            {/* Linha de Receita */}
            <tr className="border-x border-b border-border hover:bg-secondary">
              <td className="py-3 px-4 font-medium text-success-600 border-r border-border">
                Receita
              </td>
              {data.summary.map((monthData, idx) => (
                <td key={idx} className="text-right py-3 px-4 text-foreground">
                  {monthData && monthData.receita > 0 ? `R$ ${monthData.receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                </td>
              ))}
              <td className="text-right py-3 px-4 font-bold text-foreground">
                R$ {totalReceita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>

            {/* Linhas de Tipos de Receita */}
            {revenueTypes && revenueTypes.length > 0 ? revenueTypes.map((revenueType, index) => {
              const totalRevenue = revenueType.monthData.reduce((sum, item) => sum + (item?.receita || 0), 0);
              const fullName = capitalizeFirst(revenueType.typenome || revenueType.typeName || '—');
              const displayName = fullName.length > 10 ? fullName.substring(0, 7) + '...' : fullName;
              const isTruncated = fullName.length > 10;

              return (
                <tr key={`revenue-type-${revenueType.typeId}`} className="border border-border hover:bg-secondary">
                  <td className="py-2 px-4 text-sm font-medium text-blue-600 pl-6 border-r border-border">
                    <span className="text-muted">↳</span>{' '}
                    <span 
                      title={isTruncated ? fullName : undefined}
                      className={isTruncated ? 'cursor-help' : ''}
                    >
                      {displayName}
                    </span>
                  </td>
                  {revenueType.monthData && revenueType.monthData.map((monthData, idx) => (
                    <td key={idx} className="text-right py-2 px-4 text-sm text-foreground">
                      {monthData && monthData.receita > 0 ? `R$ ${monthData.receita.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                    </td>
                  ))}
                  <td className="text-right py-2 px-4 text-sm font-bold text-foreground">
                    {totalRevenue > 0 ? `R$ ${totalRevenue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                </tr>
              );
            }) : null}

            {/* Linha vazia de separação entre receitas e despesas */}
            <tr className="border border-border bg-secondary-light/30 dark:bg-secondary-dark/30">
              <td className="py-1 px-4 border-r border-border"></td>
              {months.map((month, idx) => (
                <td key={idx} className="py-1 px-4"></td>
              ))}
              <td className="py-1 px-4"></td>
            </tr>

            {/* Linha de Despesa */}
            <tr className="border-x border-b border-border hover:bg-secondary">
              <td className="py-3 px-4 font-medium text-danger-600 border-r border-border">
                Despesa
              </td>
              {data.summary.map((monthData, idx) => (
                <td key={idx} className="text-right py-3 px-4 text-foreground">
                  {monthData && monthData.despesa > 0 ? `R$ ${monthData.despesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                </td>
              ))}
              <td className="text-right py-3 px-4 font-bold text-foreground">
                R$ {totalDespesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
            </tr>

            {/* Linhas de Tipos de Despesa */}
            {expenseTypes && expenseTypes.length > 0 ? expenseTypes.map((expenseType, index) => {
              const totalExpense = expenseType.monthData.reduce((sum, item) => sum + (item?.despesa || 0), 0);
              const fullName = capitalizeFirst(expenseType.typenome || expenseType.typeName || '—');
              const displayName = fullName.length > 10 ? fullName.substring(0, 7) + '...' : fullName;
              const isTruncated = fullName.length > 10;

              return (
                <tr key={`expense-type-${expenseType.typeId}`} className="border border-border hover:bg-secondary">
                  <td className="py-2 px-4 text-sm font-medium text-orange-600 pl-6 border-r border-border">
                    <span className="text-muted">↳</span>{' '}
                    <span 
                      title={isTruncated ? fullName : undefined}
                      className={isTruncated ? 'cursor-help' : ''}
                    >
                      {displayName}
                    </span>
                  </td>
                  {expenseType.monthData && expenseType.monthData.map((monthData, idx) => (
                    <td key={idx} className="text-right py-2 px-4 text-sm text-foreground">
                      {monthData && monthData.despesa > 0 ? `R$ ${monthData.despesa.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                    </td>
                  ))}
                  <td className="text-right py-2 px-4 text-sm font-bold text-foreground">
                    {totalExpense > 0 ? `R$ ${totalExpense.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-'}
                  </td>
                </tr>
              );
            }) : null}

          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MonthlyFinancialTable;
